import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { speakSolChunk } from '../../data/sol/sol-speech-client';
import { selectAudioPlayback, type AudioPlaybackHandle } from '../../platform/audio-playback';
import { useSolSession, type SolSessionAudio } from './sol-session';
import type { SolSpeechAuthorization } from '../../data/sol/sol-contract';

/**
 * READ ALOUD — one answer, one voice, one stop. M9 / S09-04, Pass B.
 *
 * **ONE PLAYBACK FOR THE WHOLE SCREEN, AND THAT IS WHY IT IS A SCREEN-LEVEL
 * HOOK.** Two Sol answers talking over each other is not a cosmetic problem on a
 * building site, and per-answer state would make "answer B stops answer A" a
 * convention that some future control could forget. Here it is structural:
 * there is one session, and starting a new one ends the old one.
 *
 * **CHUNKS ARE GENERATED STRICTLY IN ORDER, AND ONLY AFTER THE ONE BEFORE HAS
 * FINISHED PLAYING** (D-04). Not prefetched, not pipelined, not "one ahead for
 * smoothness". Prefetching chunk 2 while chunk 1 plays would pay for audio
 * Brandon may stop before he ever hears — for a short pause between parts. That
 * is the right trade for this slice, and it is a deliberate one rather than an
 * oversight.
 *
 * **NOTHING IS EVER RETRIED AUTOMATICALLY.** Every retry is a tap.
 *
 * **A FAILURE PART-WAY THROUGH IS SAID OUT LOUD, NOT SWALLOWED.** If chunk 1
 * plays and chunk 2 fails, the session ends as `incomplete` — because stopping
 * silently would be indistinguishable from having read the whole answer, and he
 * would act on half of it believing it was all of it. **The text answer is
 * untouched and remains authoritative throughout.**
 *
 * **AUDIO IS KEPT IN MEMORY FOR THE CURRENT VISIT** (D-04-11). Replaying the
 * same answer should not buy the same audio twice. The cache is a `Map`:
 * **no database, no Storage, no filesystem, no IndexedDB, no localStorage**. It
 * holds raw bytes rather than object URLs precisely so there is no URL to leak —
 * the port mints one per play and revokes it on every terminal path.
 *
 * **THE CACHE BELONGS TO THE VISIT, NOT TO THIS HOOK, AND THAT CHANGED FOR A
 * REASON.** It used to be a ref here and was emptied on unmount — so opening a
 * source Capture and pressing Back threw away audio that had already been paid
 * for, on the same journey that threw away the answer itself. It now lives in
 * `sol-session.tsx` and ends when the visit ends: Start over, a different
 * Project, sign-out, or a refresh. **Unmounting still stops playback and aborts
 * anything in flight** — it simply no longer destroys what was bought.
 */

export type ReadAloudState = 'preparing' | 'playing' | 'failed' | 'incomplete';

export interface ReadAloudSession {
  /** The exchange this session belongs to. Nothing else may be playing. */
  readonly answerId: number;
  readonly state: ReadAloudState;
  /** The chunk being prepared, playing, or that failed. */
  readonly index: number;
}

export interface SolReadAloud {
  readonly session: ReadAloudSession | null;
  /** Begin, or restart, reading one answer. A second tap on a live one is ignored. */
  readonly start: (answerId: number, speech: SolSpeechAuthorization) => void;
  /** Retry the chunk that failed. **Never re-asks Sol and never re-reasons.** */
  readonly retry: () => void;
  /** Stop everything, immediately. Safe at any time. */
  readonly stop: () => void;
}

export function useSolReadAloud(): SolReadAloud {
  const port = useMemo(() => selectAudioPlayback(), []);
  const [session, setSession] = useState<ReadAloudSession | null>(null);
  // Owned by the visit. See the header.
  const cache = useSolSession().audio;

  /**
   * The playback generation. **Every asynchronous continuation checks it.** Stop
   * bumps it, a new answer bumps it, unmount bumps it — so a late fetch, a late
   * `ended` and a late `error` from an abandoned session all land, find they are
   * stale, and do nothing. Without it, an `onEnded` from a stopped clip would
   * cheerfully start paying for the next chunk.
   */
  const run = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const handle = useRef<AudioPlaybackHandle | null>(null);
  const mounted = useRef(true);
  /** What is currently being read, so Retry needs no argument and no new answer. */
  const active = useRef<{ answerId: number; speech: SolSpeechAuthorization } | null>(null);

  const halt = useCallback(() => {
    run.current += 1;
    controller.current?.abort();
    controller.current = null;
    handle.current?.stop();
    handle.current = null;
    // The port stops the element and revokes its object URL; calling stop here
    // as well covers the case where no handle was ever handed back.
    port.stop();
  }, [port]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      run.current += 1;
      controller.current?.abort();
      controller.current = null;
      handle.current?.stop();
      handle.current = null;
      port.stop();
      // **PLAYBACK STOPS; THE PAID AUDIO DOES NOT.** The cache belongs to the
      // visit now, so leaving to check a source Capture no longer discards it.
      // It is emptied by Start over, a Project change, sign-out or a refresh.
    };
  }, [port]);

  /** Play one clip and resolve when it genuinely finishes, or fails. */
  const playClip = useCallback(
    (clip: SolSessionAudio): Promise<'ended' | 'failed'> =>
      new Promise((resolve) => {
        handle.current = port.play({
          bytes: clip.bytes,
          mimeType: clip.mimeType,
          onEnded: () => {
            resolve('ended');
          },
          onError: () => {
            resolve('failed');
          },
        });
      }),
    [port],
  );

  /**
   * Read from `index` to the end.
   *
   * **A LOOP, NOT A RECURSION, AND NOT A PIPELINE.** Each iteration fetches one
   * chunk and then waits for it to finish playing before the next fetch is even
   * considered — which is what makes "no prefetch" a property of the control
   * flow rather than a comment somebody could later optimise away.
   */
  const playFrom = useCallback(
    async (answerId: number, speech: SolSpeechAuthorization, from: number, mine: number) => {
      for (let index = from; index < speech.chunks.length; index += 1) {
        if (!mounted.current || mine !== run.current) return;
        const chunkText = speech.chunks[index];
        if (chunkText === undefined) return;

        const key = `${String(answerId)}:${String(index)}`;
        let audio: SolSessionAudio | undefined = cache.get(key);

        if (audio === undefined) {
          setSession({ answerId, state: 'preparing', index });
          const inFlight = new AbortController();
          controller.current = inFlight;
          const outcome = await speakSolChunk({
            ticket: speech.ticket,
            chunkIndex: index,
            chunkText,
            signal: inFlight.signal,
          });
          if (!mounted.current || mine !== run.current) return;
          if (outcome.status !== 'spoken') {
            // **ABORTED IS NOT A FAILURE**, and it is stale by definition — an
            // abort only ever happens because something newer took over.
            if (outcome.status === 'aborted') return;
            // Chunk one failing is "Read Aloud is unavailable". A LATER chunk
            // failing is a different and more dangerous thing: part of the
            // answer has already been spoken.
            setSession({ answerId, state: index === 0 ? 'failed' : 'incomplete', index });
            return;
          }
          audio = { bytes: outcome.bytes, mimeType: outcome.mimeType };
          cache.set(key, audio);
        }

        if (!mounted.current || mine !== run.current) return;
        setSession({ answerId, state: 'playing', index });

        const finished = await playClip(audio);
        if (!mounted.current || mine !== run.current) return;
        if (finished === 'failed') {
          setSession({ answerId, state: index === 0 ? 'failed' : 'incomplete', index });
          return;
        }
      }

      // Every chunk played. The session ends and the control returns to rest.
      if (mounted.current && mine === run.current) setSession(null);
    },
    [cache, playClip],
  );

  const start = useCallback(
    (answerId: number, speech: SolSpeechAuthorization) => {
      // **RAPID TAP DOES NOT DOUBLE-GENERATE.** A second tap on an answer that
      // is already preparing or playing is ignored outright — restarting would
      // buy the same audio a second time and cut him off mid-sentence.
      if (
        session !== null &&
        session.answerId === answerId &&
        (session.state === 'preparing' || session.state === 'playing')
      ) {
        return;
      }
      halt();
      active.current = { answerId, speech };
      const mine = run.current;
      setSession({ answerId, state: 'preparing', index: 0 });
      void playFrom(answerId, speech, 0, mine);
    },
    [halt, playFrom, session],
  );

  const retry = useCallback(() => {
    const current = session;
    const held = active.current;
    if (current === null || held === null) return;
    if (current.state !== 'failed' && current.state !== 'incomplete') return;
    // **THE SAME ANSWER AND THE SAME TICKET.** No `sol-ask` call, no reasoning
    // call, no new text. If the ticket has expired the server refuses and this
    // simply fails again — deliberately, rather than looping.
    halt();
    const mine = run.current;
    setSession({ answerId: current.answerId, state: 'preparing', index: current.index });
    void playFrom(held.answerId, held.speech, current.index, mine);
  }, [halt, playFrom, session]);

  const stop = useCallback(() => {
    halt();
    active.current = null;
    setSession(null);
  }, [halt]);

  return { session, start, retry, stop };
}
